import React, { useRef } from 'react';
import { Dialog, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Download, Printer, X } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import ExcelJS from 'exceljs';
import './timeSales.css';

function HourlySalesReport({ open, onClose, data, filters, loading }) {
    const printRef = useRef();

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `HourlySalesReport_${filters?.date}`,
    });
    
    // Consider data present if we received hourly rows (even if totals are 0)
    const hasData = Array.isArray(data) && data.length > 0;

    const formatCurrency = (value) => {
        return `Rs ${parseFloat(value || 0).toFixed(2)}`;
    };

    const downloadExcel = () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Hourly Sales Report');
        
        let currentRow = 1;

        // Title
        const titleRow = sheet.addRow(['Hourly Sales']);
        titleRow.font = { size: 16, bold: true };
        sheet.mergeCells(`A${currentRow}:P${currentRow}`);
        titleRow.getCell(1).alignment = { horizontal: 'center' };
        currentRow++;

        // Date
        const dateRow = sheet.addRow([`Date: ${filters?.date || ''}`]);
        dateRow.font = { bold: true };
        sheet.mergeCells(`A${currentRow}:P${currentRow}`);
        currentRow++;

        // Outlet
        const outletRow = sheet.addRow([`Outlet: ${filters?.branchName || ''}`]);
        outletRow.font = { bold: true };
        sheet.mergeCells(`A${currentRow}:P${currentRow}`);
        currentRow++;

        // Empty row
        sheet.addRow([]);
        currentRow++;

        // Headers
        const headers = [
            'Time', 'Bills', 'PAX', 'Dine-in Bills', 'Dine-in Sales', 'Dine-in PAX',
            'Takeaway Bills', 'Takeaway Sales', 'Takeaway PAX',
            'Delivery Bills', 'Delivery Sales', 'Delivery PAX',
            'Open Bills', 'Open Sales', 'Open PAX',
            'Total Sales'
        ];

        const headerRow = sheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' }
            };
            cell.alignment = { horizontal: 'center' };
        });
        currentRow++;

        // Data rows
        if (data && data.length > 0) {
            data.forEach(row => {
                sheet.addRow([
                    row.time,
                    row.totalBills || 0,
                    row.totalPax || 0,
                    row.dineInBills || 0,
                    row.dineInSales || 0,
                    row.dineInPax || 0,
                    row.takeawayBills || 0,
                    row.takeawaySales || 0,
                    row.takeawayPax || 0,
                    row.deliveryBills || 0,
                    row.deliverySales || 0,
                    row.deliveryPax || 0,
                    row.openBills || 0,
                    row.openSales || 0,
                    row.openPax || 0,
                    row.totalSales || 0
                ]);
                currentRow++;
            });

            // Total row
            const totals = data.reduce((acc, row) => ({
                totalBills: (acc.totalBills || 0) + (row.totalBills || 0),
                totalPax: (acc.totalPax || 0) + (row.totalPax || 0),
                dineInBills: (acc.dineInBills || 0) + (row.dineInBills || 0),
                dineInSales: (acc.dineInSales || 0) + (row.dineInSales || 0),
                dineInPax: (acc.dineInPax || 0) + (row.dineInPax || 0),
                takeawayBills: (acc.takeawayBills || 0) + (row.takeawayBills || 0),
                takeawaySales: (acc.takeawaySales || 0) + (row.takeawaySales || 0),
                takeawayPax: (acc.takeawayPax || 0) + (row.takeawayPax || 0),
                deliveryBills: (acc.deliveryBills || 0) + (row.deliveryBills || 0),
                deliverySales: (acc.deliverySales || 0) + (row.deliverySales || 0),
                deliveryPax: (acc.deliveryPax || 0) + (row.deliveryPax || 0),
                openBills: (acc.openBills || 0) + (row.openBills || 0),
                openSales: (acc.openSales || 0) + (row.openSales || 0),
                openPax: (acc.openPax || 0) + (row.openPax || 0),
                totalSales: (acc.totalSales || 0) + (row.totalSales || 0)
            }), {});

            const totalRow = sheet.addRow([
                'Total',
                totals.totalBills,
                totals.totalPax,
                totals.dineInBills,
                totals.dineInSales,
                totals.dineInPax,
                totals.takeawayBills,
                totals.takeawaySales,
                totals.takeawayPax,
                totals.deliveryBills,
                totals.deliverySales,
                totals.deliveryPax,
                totals.openBills,
                totals.openSales,
                totals.openPax,
                totals.totalSales
            ]);
            totalRow.font = { bold: true };
            totalRow.eachCell(cell => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFEB3B' }
                };
            });
        }

        // Auto-fit columns
        sheet.columns.forEach(column => {
            let maxLength = 10;
            column.eachCell({ includeEmpty: true }, cell => {
                const columnLength = cell.value ? cell.value.toString().length : 10;
                if (columnLength > maxLength) {
                    maxLength = columnLength;
                }
            });
            column.width = Math.min(Math.max(maxLength + 2, 10), 30);
        });

        // Download
        const fileName = `HourlySalesReport_${filters?.branchName?.replace(/[^a-z0-9]/gi, '_')}_${filters?.date}.xlsx`;
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            anchor.click();
            window.URL.revokeObjectURL(url);
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '8px',
                    maxHeight: '90vh'
                }
            }}
        >
            <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0 }}>Hourly Sales Report</h2>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Tooltip title="Print">
                            <IconButton
                                onClick={handlePrint}
                                disabled={loading || !hasData}
                                style={{
                                    backgroundColor: '#d2e7ff',
                                    color: '#002995',
                                    borderRadius: '4px',
                                    opacity: (loading || !hasData) ? 0.5 : 1
                                }}
                            >
                                <Printer size={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Excel">
                            <IconButton
                                onClick={downloadExcel}
                                disabled={loading || !hasData}
                                style={{
                                    backgroundColor: '#d4edda',
                                    color: '#155724',
                                    borderRadius: '4px',
                                    opacity: (loading || !hasData) ? 0.5 : 1
                                }}
                            >
                                <Download size={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton onClick={onClose}>
                                <X size={20} />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '20px' }}>
                        <CircularProgress size={50} />
                        <p style={{ color: '#666', fontSize: '16px' }}>Loading hourly sales data...</p>
                    </div>
                ) : !hasData ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '10px' }}>
                        <p style={{ color: '#999', fontSize: '18px', fontWeight: '500' }}>No Data Available</p>
                        <p style={{ color: '#666', fontSize: '14px' }}>No orders found for the selected date range and filters.</p>
                    </div>
                ) : (
                    /* Print Content */
                    <div ref={printRef} style={{ padding: '20px' }}>
                        {/* Report Header */}
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: '0 0 10px 0' }}>Hourly Sales</h2>
                            <p style={{ margin: '5px 0' }}><strong>Date:</strong> {filters?.date || ''}</p>
                            <p style={{ margin: '5px 0' }}><strong>Outlet:</strong> {filters?.branchName || ''}</p>
                        </div>

                        {/* Table */}
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Time</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Bills</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>PAX</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e3f2fd' }}>Dine-in Bills</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e3f2fd' }}>Dine-in Sales</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e3f2fd' }}>Dine-in PAX</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#fff3e0' }}>Takeaway Bills</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#fff3e0' }}>Takeaway Sales</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#fff3e0' }}>Takeaway PAX</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#f3e5f5' }}>Delivery Bills</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#f3e5f5' }}>Delivery Sales</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#f3e5f5' }}>Delivery PAX</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e8f5e9' }}>Open Bills</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e8f5e9' }}>Open Sales</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', backgroundColor: '#e8f5e9' }}>Open PAX</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>Total Sales</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data && data.length > 0 ? (
                                        <>
                                        {data.map((row, index) => (
                                            <tr key={index}>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.time}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.totalBills || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.totalPax || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.dineInBills || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(row.dineInSales)}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.dineInPax || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.takeawayBills || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(row.takeawaySales)}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.takeawayPax || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.deliveryBills || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(row.deliverySales)}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.deliveryPax || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.openBills || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(row.openSales)}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{row.openPax || 0}</td>
                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(row.totalSales)}</td>
                                            </tr>
                                        ))}
                                        {/* Total Row */}
                                        <tr style={{ backgroundColor: '#fff9c4', fontWeight: 'bold' }}>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>Total</td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.totalBills || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.totalPax || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.dineInBills || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                                {formatCurrency(data.reduce((sum, row) => sum + (row.dineInSales || 0), 0))}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.dineInPax || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.takeawayBills || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                                {formatCurrency(data.reduce((sum, row) => sum + (row.takeawaySales || 0), 0))}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.takeawayPax || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.deliveryBills || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                                {formatCurrency(data.reduce((sum, row) => sum + (row.deliverySales || 0), 0))}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.deliveryPax || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.openBills || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                                {formatCurrency(data.reduce((sum, row) => sum + (row.openSales || 0), 0))}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                {data.reduce((sum, row) => sum + (row.openPax || 0), 0)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                                                {formatCurrency(data.reduce((sum, row) => sum + (row.totalSales || 0), 0))}
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="16" style={{ border: '1px solid #ddd', padding: '20px', textAlign: 'center', color: '#999' }}>
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}
            </div>
        </Dialog>
    );
}

export default HourlySalesReport;
